import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Input, Table, Modal, Drawer, Badge, Space, Typography, List, Tag, Select } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  display: block;
  margin-top: 8px;
`;

export const TopControlBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const MerchantFilterSelect = styled(Select)`
  min-width: 260px;

  .ant-select-selector {
    height: 36px !important;
    display: flex !important;
    align-items: center;
    border-radius: 8px !important;
    background: ${(props) => getThemeValue(props, '#1a202c', '#eee')} !important;
    border: ${(props) => getThemeValue(props, '1px solid #718096', 'none')} !important;
    padding: 0 10px !important;
  }

  .ant-select-selection-search-input {
    height: 34px !important;
  }

  .ant-select-selection-placeholder,
  .ant-select-selection-item {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    line-height: 34px !important;
  }

  .ant-select-arrow,
  .ant-select-clear {
    color: ${(props) => getThemeValue(props, '#8c8c8c', '#aaa')};
  }

  /* antd only reveals the clear "x" on hover by default (opacity: 0 until
     :hover) — show it whenever a merchant is selected, same as the search
     box's always-visible clear button. */
  .ant-select-clear {
    opacity: 1 !important;
    background: ${(props) => getThemeValue(props, '#1a202c', '#eee')} !important;

    &:hover {
      color: ${(props) => getThemeValue(props, '#fff', '#666')};
    }
  }

  &:hover .ant-select-selector {
    border-color: #40a9ff !important;
  }

  &.ant-select-focused .ant-select-selector {
    background: ${(props) => getThemeValue(props, '#1a202c', '#fff')} !important;
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.4)', '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    padding: 24px;
  }
`;

export const TableCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }
`;

export const StyledTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    font-weight: 600;
  }

  .ant-table-tbody > tr {
    transition: background-color 0.2s;

    &:hover {
      background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
`;

export const ActionButton = styled(Button)`
  background: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    color: white !important;
  }

  &.ant-btn-dangerous {
    background: #ff4d4f !important;
    border-color: #ff4d4f !important;
    
    &:hover {
      background: #ff7875 !important;
      border-color: #ff7875 !important;
    }
  }
`;

export const PrimaryButton = styled(Button)`
  background: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    color: white !important;
    box-shadow: 0 4px 12px rgba(45, 108, 202, 0.3);
  }
`;

export const DangerButton = styled(Button)`
  background: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    color: white !important;
    transform: scale(1.05);
  }

  &.ant-btn-dangerous {
    background: #ff4d4f !important;
    
    &:hover {
      background: #ff7875 !important;
    }
  }
`;

export const StyledFormSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    border-radius: 8px !important;
    border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')} !important;
    background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')} !important;
  }

  .ant-select-selection-item {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  .ant-select-selection-placeholder {
    color: ${(props) => getThemeValue(props, 'rgba(255, 255, 255, 0.45)', 'rgba(0, 0, 0, 0.45)')} !important;
  }

  .ant-select-selection-search-input {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  &:hover .ant-select-selector {
    border-color: #40a9ff !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: #1890ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
  }
`;

export const StyledInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};

  &:hover {
    border-color: #40a9ff;
  }

  &:focus {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
  }

  .ant-modal-header {
    border-radius: 12px 12px 0 0;
    flex-shrink: 0;
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-modal-body {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    overflow-y: auto;
    max-height: calc(90vh - 200px);
    padding-right: 5px;
    padding-left: 5px;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')};
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${(props) => getThemeValue(props, '#434343', '#bfbfbf')};
      border-radius: 3px;
      
      &:hover {
        background: ${(props) => getThemeValue(props, '#595959', '#999999')};
      }
    }
  }

  .ant-modal-footer {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    border-radius: 0 0 12px 12px;
    flex-shrink: 0;
  }
`;

export const StyledDrawer = styled(Drawer)`
  .ant-drawer-content {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  }

  .ant-drawer-header {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#f0f0f0')};
  }

  .ant-drawer-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-drawer-close {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-drawer-body {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${(props) => getThemeValue(props, '#434343', '#bfbfbf')};
      border-radius: 3px;

      &:hover {
        background: ${(props) => getThemeValue(props, '#595959', '#999999')};
      }
    }
  }

  .ant-drawer-footer {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    border-top: 1px solid ${(props) => getThemeValue(props, '#30363d', '#f0f0f0')};
  }
`;

export const ImagePreviewWrapper = styled.div<{ $isDark?: boolean }>`
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 2px dashed ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#fafafa')};
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:has(img) {
    border: none;
    background: none;
  }
`;

export const ImageUploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px;
  border-radius: 20px;
  border: none;
  background: #2d6cca;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  height: 28px;
  transition: background 0.2s;

  &:hover {
    background: #2560b8;
  }
`;

export const ImageEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 16px;
  cursor: pointer;
  width: 100%;
`;

export const ImageEmptyIcon = styled.span`
  font-size: 36px;
  color: ${(props) => getThemeValue(props, '#484f58', '#c0c0c0')};
`;

export const ImageEmptyText = styled.span`
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
`;

export const ImagePreviewImg = styled.img`
  width: 100%;
  height: 260px;
  object-fit: cover;
  display: block;
  background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')};
`;

export const ImageActionButtons = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
`;

export const ImageHint = styled.p`
  font-size: 11px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  margin: 6px 0 0;
`;

export const DeleteImageButton = styled(Button)`
  border-radius: 20px !important;
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

// --- Store table thumbnail + preview (mirrors the Items tab's pattern) ---

export const StoreNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const StoreThumbnailWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
`;

export const StoreThumbnailImg = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
`;

export const StoreNoImagePlaceholder = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')};
  border: 1px dashed ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => getThemeValue(props, '#6e7681', '#c0c0c0')};
  flex-shrink: 0;
`;

export const StoreEyeBadge = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1;
`;

export const StorePreviewImageWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const StorePreviewImg = styled.img`
  max-width: 80vw;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
`;

export const CategoryBadge = styled(Badge)`
  .ant-badge {
    border-radius: 16px;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
`;

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

export const EmptyStateText = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')} !important;
  display: block;
  margin-top: 8px;
`;

export const LoadingContainer = styled.div`
  padding: 48px;
  text-align: center;
`;

export const AddressText = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-top: 4px;
  
  svg {
    margin-right: 4px;
  }
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SelectedItemsContainer = styled.div`
  padding: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  border-radius: 8px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#fafafa')};
  margin-top: 16px;
`;

export const SelectedItemsWrap = styled(Space)`
  margin-top: 12px;
  flex-wrap: wrap;
`;

export const InfoBox = styled.div`
  padding: 12px;
  background: ${(props) => getThemeValue(props, 'rgba(24, 144, 255, 0.1)', 'rgba(24, 144, 255, 0.05)')};
  border: 1px solid ${(props) => getThemeValue(props, 'rgba(24, 144, 255, 0.3)', 'rgba(24, 144, 255, 0.2)')};
  border-radius: 8px;
  margin-top: 16px;
`;

export const InfoText = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#58a6ff', '#1890ff')} !important;
`;

export const StyledTag = styled(Tag)`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
`;

export const DropdownContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

export const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  border-radius: 8px;
  margin-top: 8px;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.1)')};
  
  .ant-list {
    background: transparent;
    border-radius: 8px;
  }
  
  .ant-list-item {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#f0f0f0')} !important;
    padding: 12px 16px !important;
    
    &:last-child {
      border-bottom: none !important;
    }
  }
`;

export const SectionTitle = styled(Typography.Title)`
  margin-bottom: 12px !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600 !important;
`;

export const SecondaryText = styled(Typography.Text)`
  display: block;
  margin-top: 8px;
`;

export const IconSpacer = styled.span`
  margin-right: 4px;
`;

export const ScheduleTimeText = styled.div`
  font-size: 12px;
  opacity: 0.7;
`;

export const ListItemContent = styled(Space)`
  width: 100%;
`;

export const ListItemSelected = styled(List.Item)<{ $isSelected: boolean }>`
  cursor: pointer;
  padding: 12px 16px !important;
  background-color: ${(props) => 
    props.$isSelected 
      ? getThemeValue(props, 'rgba(45, 108, 202, 0.15)', 'rgba(0, 0, 0, 0.05)')
      : 'transparent'
  } !important;
  transition: background-color 0.2s;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;

  &:hover {
    background-color: ${(props) => 
      props.$isSelected 
        ? getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(0, 0, 0, 0.08)')
        : getThemeValue(props, 'rgba(255, 255, 255, 0.05)', 'rgba(0, 0, 0, 0.04)')
    } !important;
  }
`;

export const LastUpdatedContainer = styled.div`
  text-align: right;
`;

export const LastUpdatedLabel = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 4px;
`;

export const LastUpdatedValue = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

export const TableWrapper = styled.div`
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  padding: 12px;
  border-radius: 12px;
`;

export const StyledButton = styled(Button)`
  background: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    border-color: #2560b8 !important;
    color: white !important;
  }
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  border: 0 !important;
  border-radius: 8px !important;
  color: #121212 !important;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 16px !important;
  font-weight: 500 !important;
  margin-left: 24px;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: #d9d9d9 !important;
    color: #121212 !important;
    border: 0 !important;
  }

  &:focus {
    background-color: #ededed !important;
    color: #121212 !important;
    border: 0 !important;
  }
`;

export const ErrorText = styled(Typography.Text)`
  color: #ff4d4f !important;
  font-size: 12px;
  display: block;
  margin-top: 4px;
`;

export const FieldLabel = styled.label`
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
`;

export const TitleIcon = styled.span`
  margin-right: 12px;
`;

export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#eee')};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, 'transparent', 'inherit')};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || '#2D6CCA';
      if (iconColor === '#f5222d') {
        return getThemeValue(props, 'rgba(245, 34, 45, 0.2)', 'rgba(245, 34, 45, 0.1)');
      }
      return getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(45, 108, 202, 0.1)');
    }} !important;

    svg {
      color: ${(props) => props.iconColor || '#2D6CCA'} !important;
    }
  }
`;

// --- Bulk auto-accept actions (dropdown trigger + panel) ---

export const BulkActionsTrigger = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  background: ${(props) => getThemeValue(props, 'rgba(45, 108, 202, 0.18)', '#E6F0FB')};
  color: #2D6CCA;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: ${(props) => getThemeValue(props, 'rgba(45, 108, 202, 0.28)', '#D6E7F8')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg:last-child {
    transition: transform 0.2s;
    transform: rotate(${(props) => (props.$active ? '180deg' : '0deg')});
  }
`;

export const BulkActionsPanel = styled.div`
  width: 300px;
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  border-radius: 12px;
  box-shadow: ${(props) => getThemeValue(props, '0 8px 24px rgba(0, 0, 0, 0.4)', '0 8px 24px rgba(0, 0, 0, 0.12)')};
  padding: 8px;
`;

export const BulkActionRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s;
  background: ${(props) =>
    props.$active ? getThemeValue(props, 'rgba(45, 108, 202, 0.16)', '#E6F0FB') : 'transparent'};

  &:hover {
    background: ${(props) =>
      props.$active
        ? getThemeValue(props, 'rgba(45, 108, 202, 0.22)', '#D6E7F8')
        : getThemeValue(props, 'rgba(255, 255, 255, 0.06)', 'rgba(0, 0, 0, 0.04)')};
  }
`;

export const BulkActionIconBox = styled.div<{ $variant: 'enable' | 'disable' }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background: ${(props) =>
    props.$variant === 'enable'
      ? getThemeValue(props, 'rgba(45, 108, 202, 0.25)', '#E6F0FB')
      : getThemeValue(props, 'rgba(255, 255, 255, 0.08)', '#F1F1F3')};
  color: ${(props) => (props.$variant === 'enable' ? '#2D6CCA' : getThemeValue(props, '#8b949e', '#9CA3AF'))};
`;

export const BulkActionTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const BulkActionSubtitle = styled.div`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#6b7280')};
  margin-top: 2px;
`;

export const BulkActionsDivider = styled.div`
  height: 1px;
  background: ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  margin: 4px 0;
`;

export const BulkActionsFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#6b7280')};
`;

// --- Bulk auto-accept confirm dialog ---

export const BulkConfirmModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 32px 28px;
    text-align: center;
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  }

  .ant-modal-body {
    padding: 0;
  }
`;

export const BulkConfirmIconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) => getThemeValue(props, 'rgba(250, 173, 20, 0.15)', '#FEF3E2')};
  color: #fa8c16;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 20px;
`;

export const BulkConfirmTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  margin: 0 0 12px;
`;

export const BulkConfirmDescription = styled.p`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#6b7280')};
  margin: 0 0 24px;
  line-height: 1.5;
`;

export const BulkConfirmPrimaryButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  background: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  margin-bottom: 12px;

  &:hover {
    background: #2560b8 !important;
    border-color: #2560b8 !important;
    color: #fff !important;
  }
`;

export const BulkConfirmCancelButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  background: transparent !important;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')} !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600 !important;
  font-size: 15px !important;

  &:hover {
    background: ${(props) => getThemeValue(props, 'rgba(255, 255, 255, 0.06)', '#f5f5f5')} !important;
    border-color: ${(props) => getThemeValue(props, '#595959', '#bfbfbf')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }
`;
